/**
 * GeminiWarnung – Welle 104.1: Gemini gesperrt (Guthaben aufgebraucht, Tageslimit,
 * kein Schlüssel) muss ÜBERALL zu sehen sein, wo Gemini benutzt wird.
 *
 * Eine gemeinsame Abfrage für alle Stellen (alle 60 s), damit nicht jede Seite selbst pollt.
 * Der Zustand kommt vom Backend (`ki/gemini_status.py`), das ihn bei jedem Gemini-Aufruf
 * mitschreibt — auch aus dem Rename-me-Batch heraus.
 *
 *   useGeminiSperre()          → { gesperrt, art, text, seit, fehler } oder null
 *   <GeminiBalken />           → schmaler roter Balken unter der Navigation (alle Seiten)
 *   <GeminiWarnung gross />    → großer Block (Postkorb, Automatik, Rename-me, KRV)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE } from "../api/config";

const URL = `${API_BASE}/postkorb/api/gemini-sperre`;
let stand = null;
let timer = null;
const hoerer = new Set();

async function holen() {
  try {
    const r = await fetch(URL, { credentials: "include", headers: { Accept: "application/json" } });
    if (r.ok) {
      stand = await r.json();
      hoerer.forEach((f) => f(stand));
    }
  } catch (_) { /* Backend nicht erreichbar → alten Stand behalten */ }
}

/** Sofort neu abfragen (z. B. nach „Jetzt testen" im Postkorb). */
export function geminiSperreNeuLaden() { return holen(); }

export function useGeminiSperre() {
  const [s, setS] = useState(stand);
  useEffect(() => {
    hoerer.add(setS);
    if (!timer) {
      holen();
      timer = setInterval(holen, 60000);
    }
    return () => {
      hoerer.delete(setS);
      if (!hoerer.size && timer) { clearInterval(timer); timer = null; }
    };
  }, []);
  return s;
}

const seitText = (seit) => {
  if (!seit) return "";
  const d = new Date(seit);
  if (isNaN(d)) return "";
  return ` seit ${d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}, ` +
         `${d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;
};

const ABHILFE = {
  guthaben: "Guthaben im Google AI Studio (ai.studio/projects) aufladen — danach geht es innerhalb von 15 Minuten von selbst weiter.",
  tageslimit: "Morgen geht es von selbst weiter.",
  schluessel: "GEMINI_API_KEY_ZAEHLER in der .env des Servers eintragen.",
};

export function GeminiBalken() {
  const s = useGeminiSperre();
  if (!s || !s.gesperrt) return null;
  return (
    <div role="alert" style={{
      background: "var(--color-error)", color: "#fff", padding: "7px 16px", fontSize: "0.85rem",
      fontWeight: 600, display: "flex", gap: 10, alignItems: "center", justifyContent: "center",
      flexWrap: "wrap", textAlign: "center",
    }}>
      <span>⚠ {s.text}{seitText(s.seit)} — der Rename-me-Batch läuft nicht, nichts wird mit Gemini ausgewertet.</span>
      <Link to="/postkorb" style={{ color: "#fff", textDecoration: "underline" }}>Details im Postkorb</Link>
    </div>
  );
}

export default function GeminiWarnung({ gross = false, wofuer = "" }) {
  const s = useGeminiSperre();
  if (!s || !s.gesperrt) return null;
  return (
    <div role="alert" style={{
      border: "2px solid var(--color-error)", background: "var(--color-error-bg)", color: "var(--color-error)",
      borderRadius: 10, padding: gross ? "16px 20px" : "10px 14px", margin: "0 0 16px",
      display: "flex", gap: 14, alignItems: "flex-start",
    }}>
      <span style={{ fontSize: gross ? "2rem" : "1.3rem", lineHeight: 1 }}>⚠</span>
      <div>
        <div style={{ fontSize: gross ? "1.25rem" : "1rem", fontWeight: 800 }}>
          {s.text}{seitText(s.seit)}
        </div>
        <div style={{ fontSize: "0.88rem", marginTop: 4, color: "var(--color-gray-800)" }}>
          {wofuer || "Der Rename-me-Batch läuft nicht, bis Gemini wieder geht. Einzelne Dokumente werden nur mit v1 benannt, KRV-Abrechnungen und Belege werden nicht ausgewertet."}
        </div>
        <div style={{ fontSize: "0.85rem", marginTop: 4, color: "var(--color-gray-700)" }}>
          {ABHILFE[s.art] || ""}
        </div>
        {gross && s.fehler && (
          <div style={{ fontSize: "0.72rem", marginTop: 6, color: "var(--color-gray-500)", wordBreak: "break-all" }}>
            {String(s.fehler).slice(0, 220)}
          </div>
        )}
      </div>
    </div>
  );
}
